/*
 * Copyright 2022 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     https://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

/**
 * External dependencies
 */
import { trackEvent } from '@googleforcreators/tracking';
import { useCallback } from '@googleforcreators/react';

/**
 * Internal dependencies
 */
import useRichTextFormatting from '../../../panels/design/textStyle/useRichTextFormatting';
import updateProperties from '../../../style/updateProperties';
import { useStory } from '../../../../app';
import getUpdatedSizeAndPosition from '../../../../utils/getUpdatedSizeAndPosition';
import useProperties from './useProperties';

function useTextToggle({ currentValue, handler, eventName }) {
  const { content } = useProperties(['content']);

  const { selectedElementIds, updateElementsById } = useStory(
    ({ state, actions }) => ({
      selectedElementIds: state.selectedElementIds,
      updateElementsById: actions.updateElementsById,
    })
  );

  const pushUpdate = useCallback(
    (update) => {
      void trackEvent('floating_menu', {
        name: eventName,
        element: 'text',
      });
      updateElementsById({
        elementIds: selectedElementIds,
        properties: (element) => {
          const updates = updateProperties(element, update, true);
          const sizeUpdates = getUpdatedSizeAndPosition({
            ...element,
            ...updates,
          });
          return {
            ...updates,
            ...sizeUpdates,
          };
        },
      });
    },
    [updateElementsById, selectedElementIds, eventName]
  );
  const { textInfo, handlers } = useRichTextFormatting(
    [{ content, type: 'text' }],
    pushUpdate
  );

  const toggle = () => {
    const fn = handlers[handler];
    fn(!textInfo[currentValue]);
  };

  return {
    isToggled: textInfo[currentValue],
    toggle,
  };
}

export default useTextToggle;
